import React, { useState } from 'react';
import { BrowserView, MobileOnlyView, TabletView } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SVGBack from '../../common/assets/svg/icons/SVGBack';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import Log from '../../common/logging';
import { Routes } from '../../config/routes';
import { useRevocation } from '../../providers/RevocationContext';
import { ServiceErrorType, UserResponseOptions } from '../../services/common/serviceError';
import ServiceErrorProvider from '../../services/common/serviceErrorProvider';
import CameraChooser from './components/CameraChooser';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';
import { ComplianceStoreCheckError, isComplianceStoreCheckError } from '../../services/faceAuthentication/errors';

function FaceSearchScreen(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // When a user clicks `Use your webcam` theres an awkward delay before facetec spins up, so we hide the buttons to show something is happening.
  const [hasSelectedWeb, setHasSelectedWeb] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { fetchRevocationPasses } = useRevocation();

  const handleConfirm = async () => {
    setLoading(true);
    setHasSelectedWeb(true);
    try {
      await fetchRevocationPasses();
      navigate(Routes.REVOCATION_PASSES_LIST);
    } catch (e) {
      setHasSelectedWeb(false); // Make the mobile/webcam buttons appear again

      if (isComplianceStoreCheckError(e as Error)) {
        navigate(Routes.ERROR, {
          state: { errorCode: (e as ComplianceStoreCheckError)?.getComplianceStoreCheckResult()?.errorCode },
        });
        return;
      }

      const notifyUserOptions = UserResponseOptions.fail;
      await ServiceErrorProvider.notifyUser(ServiceErrorType.network, notifyUserOptions);
      const error = e as Error;
      Log.error(error.message);
    }
    setLoading(false);
  };

  const handlePhoneContinue = () => {
    navigate(Routes.REVOCATION_QR_CODE);
  };

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  const renderMobileView = () => {
    return (
      <>
        <TitleContainer center>
          <h1 data-testid="FACE_ISSUANCE_HEADER">{t('screens.faceSearch.title')}</h1>
          <div className="text-xl leading-6 my-4 text-textParagraph" data-testid="FACE_ISSUANCE_DESCRIPTION">
            <p>{t('screens.faceSearch.description')}</p>
          </div>
        </TitleContainer>
        <ButtonContainer>
          <Button
            style={ButtonStyle.DEFAULT}
            onClick={handleConfirm}
            disabled={loading}
            loading={loading}
            dataTestid="OK_BUTTON"
          >
            <span>{t('screens.faceUniqueness.buttonLabel')}</span>
          </Button>
        </ButtonContainer>
      </>
    );
  };

  const renderWebview = () => {
    return !hasSelectedWeb ? (
      <CameraChooser
        title={t('screens.faceSearch.title')}
        handleChoosePhone={handlePhoneContinue}
        handleChooseWebcam={handleConfirm}
        handleChooseBreakoutFlow={() => {}}
        description={{ text: t('screens.faceSearch.description') }}
      />
    ) : (
      <div className="my-32">
        <div className="pass-loading-spinner" />
      </div>
    );
  };

  return (
    <Container>
      <>
        <div className="items-left w-full">
          <Button
            style={ButtonStyle.INVERTED}
            onClick={handleBackButtonClick}
            disabled={false}
            extraClasses="flex-grow back-button ml-4"
            dataTestid="BACK_BUTTON"
          >
            <div className="flex text-textTertiary uppercase text-xs flex-grow items-center">
              <SVGBack />
            </div>
          </Button>
        </div>
        <BrowserView className="w-full flex flex-col items-center">{renderWebview()}</BrowserView>
        <TabletView className="w-full">{renderWebview()}</TabletView>
        <MobileOnlyView>{renderMobileView()}</MobileOnlyView>
      </>
    </Container>
  );
}

export default withRouteAnalytics(FaceSearchScreen, Screens.REVOCATION_FACE_SEARCH);
